import { useEffect, useState } from "react";

export interface ClockState {
  hourAngle: number;
  minAngle: number;
  secAngle: number;
  digital: string;
  /** Hour (0–23) in the configured timezone (persona's location). */
  hour: number;
  /** Hour (0–23) on the VIEWER's own device — drives the day/night phase. */
  localHour: number;
}

/**
 * Live clock for a given IANA timezone.
 *
 * Angles are returned *monotonically increasing* across the day (e.g. the
 * second hand keeps climbing past 360°) so a spring-animated hand ticks
 * forward instead of unwinding a full turn every minute. Values are still
 * visually correct because they're equivalent mod 360.
 */
export function useClock(timezone: string): ClockState {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: timezone,
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(now);

  const get = (type: string): number => {
    const p = parts.find((x) => x.type === type);
    return p ? parseInt(p.value, 10) : 0;
  };

  const h = get("hour") % 24;
  const m = get("minute");
  const s = get("second");
  const pad = (n: number) => String(n).padStart(2, "0");

  return {
    hourAngle: (h + m / 60 + s / 3600) * 30, // monotonic over 24h
    minAngle: (h * 60 + m + s / 60) * 6, // monotonic over 24h
    secAngle: (h * 3600 + m * 60 + s) * 6, // monotonic over 24h
    digital: `${pad(h)}:${pad(m)}:${pad(s)}`,
    hour: h,
    localHour: now.getHours(),
  };
}
